/**
 * Art-direction–specific spec builders for OG & share images.
 *
 * Each builder produces a ThumbnailSpecV1 with distinct visual treatment:
 *   - editorial-dark:       Dark moody editorial (default for OG)
 *   - clean-analytic:       Light, clinical, data-forward
 *   - alert-high-contrast:  Bold warning palette for extreme scores
 *   - gradient-drama:       Gradient sweep, narrative-first (rewrite journey)
 *
 * All builders accept the same OGImageData + optional config, differing only
 * in color tokens, layout emphasis, and metadata.artDirection.
 *
 * @see DRAMA-Sprint-12 Wave 3 — T1: Build spec-based template builders
 */

import { CATEGORY_COLORS, CATEGORY_LABELS } from "@dramadetector/shared/og";
import type { DramaCategory } from "@dramadetector/shared/og";
import type {
	ThumbnailSpecV1,
	ArtDirection,
	EmphasisMode,
	SpecElement,
} from "./thumbnail-spec-v1";
import type { OGImageData } from "./og-image-spec";

// ============================================================================
// Shared types & helpers
// ============================================================================

export interface SpecBuilderConfig {
	showScore?: boolean;
	showSummary?: boolean;
	footerText?: string;
	maxSummaryLength?: number;
	format?: "horizontal" | "square" | "portrait" | "reel";
	surface?: "og-image" | "share-image";
	templateId?: string;
	candidateId?: string;
}

const DEFAULT_CONFIG: Required<
	Omit<SpecBuilderConfig, "templateId" | "candidateId">
> = {
	showScore: true,
	showSummary: true,
	footerText: "AI-Powered Communication Analysis",
	maxSummaryLength: 200,
	format: "horizontal",
	surface: "og-image",
};

/** Format → canvas dimensions */
const FORMAT_DIMS: Record<string, { width: number; height: number }> = {
	horizontal: { width: 1200, height: 630 },
	square: { width: 1080, height: 1080 },
	portrait: { width: 1080, height: 1350 },
	reel: { width: 1080, height: 1920 },
};

/** Truncate text with ellipsis */
function truncate(text: string, max: number): string {
	return text.length > max ? `${text.slice(0, max - 3)}...` : text;
}

/** Build common metadata */
function buildMetadata(
	data: OGImageData,
	cfg: ReturnType<typeof mergeConfig>,
	artDirection: ArtDirection,
	emphasisMode: EmphasisMode,
): ThumbnailSpecV1["metadata"] {
	return {
		artDirection,
		emphasisMode,
		format: cfg.format,
		surface: cfg.surface,
		templateId: cfg.templateId,
		candidateId: cfg.candidateId,
		content: {
			score: data.score,
			category: data.category,
			summary: data.summary,
			patternsCount: data.patternsCount,
		},
	};
}

function mergeConfig(config?: SpecBuilderConfig) {
	return { ...DEFAULT_CONFIG, ...config };
}

function getDims(format: string) {
	return FORMAT_DIMS[format] ?? FORMAT_DIMS.horizontal;
}

// ============================================================================
// editorial-dark — Dark moody editorial (default OG)
// ============================================================================

const EDITORIAL_DARK_TOKENS = {
	bg: "#0F172A", // slate-900
	text: "#F8FAFC", // slate-50
	textMuted: "#94A3B8", // slate-400
	accent: "#F59E0B", // amber-500
	divider: "rgba(248, 250, 252, 0.1)",
	scoreBg: "rgba(245, 158, 11, 0.15)",
};

export function buildEditorialDarkSpec(
	data: OGImageData,
	config?: SpecBuilderConfig,
): ThumbnailSpecV1 {
	const cfg = mergeConfig(config);
	const dims = getDims(cfg.format);
	const category = data.category || ("safe" as DramaCategory);
	const categoryLabel = CATEGORY_LABELS[category] ?? "Analysis";
	const summary = truncate(
		data.summary || "Communication analysis by DramaDetector",
		cfg.maxSummaryLength,
	);

	const elements: Record<string, SpecElement> = {};
	const rootChildren: string[] = [];

	// Header: brand + category
	elements["header-brand"] = {
		type: "Text",
		props: {
			text: "DramaDetector",
			fontSize: 28,
			fontWeight: "bold",
			color: EDITORIAL_DARK_TOKENS.text,
			letterSpacing: "-0.02em",
		},
		children: [],
	};
	elements["header-badge"] = {
		type: "DramaCategoryBadge",
		props: { category },
		children: [],
	};
	elements["header"] = {
		type: "Row",
		props: { justifyContent: "space-between", alignItems: "center" },
		children: ["header-brand", "header-badge"],
	};
	rootChildren.push("header");
	rootChildren.push(spacer(elements, "s1", 40));

	// Score
	if (cfg.showScore) {
		elements["score"] = {
			type: "DramaScore",
			props: {
				score: data.score,
				category,
				size: "lg",
				showLabel: true,
				backgroundColor: EDITORIAL_DARK_TOKENS.scoreBg,
			},
			children: [],
		};
		elements["score-label"] = {
			type: "Text",
			props: {
				text: `${categoryLabel} Level`,
				fontSize: 20,
				color: EDITORIAL_DARK_TOKENS.accent,
				fontWeight: "600",
			},
			children: [],
		};
		elements["score-row"] = {
			type: "Row",
			props: { alignItems: "center", gap: 28 },
			children: ["score", "score-label"],
		};
		rootChildren.push("score-row");
		rootChildren.push(spacer(elements, "s2", 24));
	}

	// Summary
	if (cfg.showSummary) {
		elements["summary"] = {
			type: "NarrativeBlock",
			props: {
				text: summary,
				emphasis: category === "extreme_drama" ? "critical" : "normal",
				color: EDITORIAL_DARK_TOKENS.textMuted,
			},
			children: [],
		};
		elements["summary-box"] = {
			type: "Box",
			props: { flex: 1 },
			children: ["summary"],
		};
		rootChildren.push("summary-box");
	}

	// Patterns
	if (data.patternsCount && data.patternsCount > 0) {
		elements["patterns"] = {
			type: "Text",
			props: {
				text: `${data.patternsCount} pattern${data.patternsCount === 1 ? "" : "s"} detected`,
				fontSize: 14,
				color: EDITORIAL_DARK_TOKENS.accent,
				fontWeight: "bold",
			},
			children: [],
		};
		rootChildren.push("patterns");
	}

	// Footer
	appendFooter(
		elements,
		rootChildren,
		cfg.footerText,
		EDITORIAL_DARK_TOKENS.divider,
		EDITORIAL_DARK_TOKENS.textMuted,
		"rgba(148, 163, 184, 0.6)",
	);

	// Root
	elements["root"] = {
		type: "Frame",
		props: {
			...dims,
			backgroundColor: EDITORIAL_DARK_TOKENS.bg,
			padding: 60,
			display: "flex",
			flexDirection: "column",
		},
		children: rootChildren,
	};

	return {
		version: 1,
		root: "root",
		elements,
		metadata: buildMetadata(data, cfg, "editorial-dark", "score-first"),
	};
}

// ============================================================================
// clean-analytic — Light, clinical, data-forward
// ============================================================================

const CLEAN_ANALYTIC_TOKENS = {
	bg: "#FFFFFF",
	text: "#1F2937", // gray-800
	textMuted: "#6B7280", // gray-500
	accent: "#3B82F6", // blue-500
	divider: "#E5E7EB", // gray-200
};

export function buildCleanAnalyticSpec(
	data: OGImageData,
	config?: SpecBuilderConfig,
): ThumbnailSpecV1 {
	const cfg = mergeConfig(config);
	const dims = getDims(cfg.format);
	const category = data.category || ("safe" as DramaCategory);
	const colors = CATEGORY_COLORS[category] ?? CATEGORY_COLORS.safe;
	const categoryLabel = CATEGORY_LABELS[category] ?? "Analysis";
	const summary = truncate(
		data.summary || "Communication analysis by DramaDetector",
		cfg.maxSummaryLength,
	);

	const elements: Record<string, SpecElement> = {};
	const rootChildren: string[] = [];

	// Accent top bar (category-colored strip)
	elements["accent-bar"] = {
		type: "Box",
		props: {
			height: 6,
			backgroundColor: colors.accent,
			borderRadius: 3,
		},
		children: [],
	};
	rootChildren.push("accent-bar");
	rootChildren.push(spacer(elements, "s0", 24));

	// Header
	elements["header-brand"] = {
		type: "Text",
		props: {
			text: "DramaDetector",
			fontSize: 30,
			fontWeight: "bold",
			color: CLEAN_ANALYTIC_TOKENS.text,
			letterSpacing: "-0.02em",
		},
		children: [],
	};
	elements["header-badge"] = {
		type: "DramaCategoryBadge",
		props: { category },
		children: [],
	};
	elements["header"] = {
		type: "Row",
		props: { justifyContent: "space-between", alignItems: "center" },
		children: ["header-brand", "header-badge"],
	};
	rootChildren.push("header");
	rootChildren.push(spacer(elements, "s1", 32));

	// Category-first: big category label, then score
	elements["cat-label"] = {
		type: "Text",
		props: {
			text: categoryLabel.toUpperCase(),
			fontSize: 16,
			fontWeight: "bold",
			color: colors.accent,
			letterSpacing: "0.1em",
		},
		children: [],
	};
	rootChildren.push("cat-label");
	rootChildren.push(spacer(elements, "s1b", 12));

	// Score
	if (cfg.showScore) {
		elements["score"] = {
			type: "DramaScore",
			props: {
				score: data.score,
				category,
				size: "md",
				showLabel: true,
			},
			children: [],
		};
		rootChildren.push("score");
		rootChildren.push(spacer(elements, "s2", 20));
	}

	// Summary
	if (cfg.showSummary) {
		elements["summary"] = {
			type: "NarrativeBlock",
			props: {
				text: summary,
				emphasis: "normal",
				color: CLEAN_ANALYTIC_TOKENS.textMuted,
			},
			children: [],
		};
		elements["summary-box"] = {
			type: "Box",
			props: { flex: 1 },
			children: ["summary"],
		};
		rootChildren.push("summary-box");
	}

	// Patterns
	if (data.patternsCount && data.patternsCount > 0) {
		elements["patterns"] = {
			type: "InsightRow",
			props: {
				label: "Patterns Detected",
				value: String(data.patternsCount),
				color: colors.accent,
			},
			children: [],
		};
		rootChildren.push("patterns");
	}

	// Footer
	appendFooter(
		elements,
		rootChildren,
		cfg.footerText,
		CLEAN_ANALYTIC_TOKENS.divider,
		CLEAN_ANALYTIC_TOKENS.textMuted,
		"#9CA3AF",
	);

	// Root
	elements["root"] = {
		type: "Frame",
		props: {
			...dims,
			backgroundColor: CLEAN_ANALYTIC_TOKENS.bg,
			padding: 60,
			display: "flex",
			flexDirection: "column",
		},
		children: rootChildren,
	};

	return {
		version: 1,
		root: "root",
		elements,
		metadata: buildMetadata(data, cfg, "clean-analytic", "category-first"),
	};
}

// ============================================================================
// alert-high-contrast — Bold warning palette for extreme scores
// ============================================================================

const ALERT_HC_TOKENS = {
	bg: "#1C1917", // stone-900
	text: "#FAFAF9", // stone-50
	textMuted: "#D6D3D1", // stone-300
	warning: "#EF4444", // red-500
	warningMuted: "#FCA5A5", // red-300
	divider: "rgba(250, 250, 249, 0.15)",
};

export function buildAlertHighContrastSpec(
	data: OGImageData,
	config?: SpecBuilderConfig,
): ThumbnailSpecV1 {
	const cfg = mergeConfig(config);
	const dims = getDims(cfg.format);
	const category = data.category || ("safe" as DramaCategory);
	const categoryLabel = CATEGORY_LABELS[category] ?? "Analysis";
	const summary = truncate(
		data.summary || "Communication analysis by DramaDetector",
		cfg.maxSummaryLength,
	);

	const elements: Record<string, SpecElement> = {};
	const rootChildren: string[] = [];

	// Warning bar across top
	elements["warning-bar"] = {
		type: "Box",
		props: {
			height: 8,
			backgroundColor: ALERT_HC_TOKENS.warning,
		},
		children: [],
	};
	rootChildren.push("warning-bar");
	rootChildren.push(spacer(elements, "s0", 20));

	// Header
	elements["header-brand"] = {
		type: "Text",
		props: {
			text: "⚠ DramaDetector",
			fontSize: 28,
			fontWeight: "bold",
			color: ALERT_HC_TOKENS.warning,
		},
		children: [],
	};
	elements["header-badge"] = {
		type: "DramaCategoryBadge",
		props: { category },
		children: [],
	};
	elements["header"] = {
		type: "Row",
		props: { justifyContent: "space-between", alignItems: "center" },
		children: ["header-brand", "header-badge"],
	};
	rootChildren.push("header");
	rootChildren.push(spacer(elements, "s1", 36));

	// Score — large, prominent
	if (cfg.showScore) {
		elements["score"] = {
			type: "DramaScore",
			props: {
				score: data.score,
				category,
				size: "lg",
				showLabel: true,
				backgroundColor: "rgba(239, 68, 68, 0.2)",
			},
			children: [],
		};
		elements["score-context"] = {
			type: "Text",
			props: {
				text: `${categoryLabel} — Immediate Attention Recommended`,
				fontSize: 18,
				color: ALERT_HC_TOKENS.warningMuted,
				fontWeight: "600",
			},
			children: [],
		};
		elements["score-row"] = {
			type: "Row",
			props: { alignItems: "center", gap: 24 },
			children: ["score", "score-context"],
		};
		rootChildren.push("score-row");
		rootChildren.push(spacer(elements, "s2", 20));
	}

	// Summary
	if (cfg.showSummary) {
		elements["summary"] = {
			type: "NarrativeBlock",
			props: {
				text: summary,
				emphasis: "critical",
				color: ALERT_HC_TOKENS.textMuted,
			},
			children: [],
		};
		elements["summary-box"] = {
			type: "Box",
			props: { flex: 1 },
			children: ["summary"],
		};
		rootChildren.push("summary-box");
	}

	// Footer
	appendFooter(
		elements,
		rootChildren,
		cfg.footerText,
		ALERT_HC_TOKENS.divider,
		ALERT_HC_TOKENS.textMuted,
		"rgba(214, 211, 209, 0.5)",
	);

	// Root
	elements["root"] = {
		type: "Frame",
		props: {
			...dims,
			backgroundColor: ALERT_HC_TOKENS.bg,
			padding: 60,
			display: "flex",
			flexDirection: "column",
		},
		children: rootChildren,
	};

	return {
		version: 1,
		root: "root",
		elements,
		metadata: buildMetadata(data, cfg, "alert-high-contrast", "score-first"),
	};
}

// ============================================================================
// gradient-drama — Gradient sweep, narrative-first (rewrite journey)
// ============================================================================

const GRADIENT_TOKENS = {
	bgFrom: "#1E1B4B", // indigo-950
	bgTo: "#581C87", // purple-900
	text: "#F5F3FF", // violet-50
	textMuted: "#C4B5FD", // violet-300
	accent: "#A78BFA", // violet-400
	divider: "rgba(196, 181, 253, 0.2)",
};

export function buildGradientDramaSpec(
	data: OGImageData,
	config?: SpecBuilderConfig,
): ThumbnailSpecV1 {
	const cfg = mergeConfig(config);
	const dims = getDims(cfg.format);
	const category = data.category || ("safe" as DramaCategory);
	const categoryLabel = CATEGORY_LABELS[category] ?? "Analysis";
	const summary = truncate(
		data.summary || "Communication analysis by DramaDetector",
		cfg.maxSummaryLength,
	);

	const elements: Record<string, SpecElement> = {};
	const rootChildren: string[] = [];

	// Header: minimal, narrative-first means header is subdued
	elements["header-brand"] = {
		type: "Text",
		props: {
			text: "DramaDetector",
			fontSize: 24,
			fontWeight: "600",
			color: GRADIENT_TOKENS.textMuted,
			letterSpacing: "0.05em",
		},
		children: [],
	};
	elements["header-badge"] = {
		type: "TierBadge",
		props: {
			score: data.score,
			category,
			variant: "minimal",
		},
		children: [],
	};
	elements["header"] = {
		type: "Row",
		props: { justifyContent: "space-between", alignItems: "center" },
		children: ["header-brand", "header-badge"],
	};
	rootChildren.push("header");
	rootChildren.push(spacer(elements, "s1", 48));

	// Narrative-first: summary is the hero
	if (cfg.showSummary) {
		elements["summary-label"] = {
			type: "Text",
			props: {
				text: "ANALYSIS SUMMARY",
				fontSize: 14,
				fontWeight: "bold",
				color: GRADIENT_TOKENS.accent,
				letterSpacing: "0.12em",
			},
			children: [],
		};
		rootChildren.push("summary-label");
		rootChildren.push(spacer(elements, "s1b", 16));

		elements["summary"] = {
			type: "NarrativeBlock",
			props: {
				text: summary,
				emphasis: "featured",
				color: GRADIENT_TOKENS.text,
				fontSize: 22,
			},
			children: [],
		};
		elements["summary-box"] = {
			type: "Box",
			props: { flex: 1 },
			children: ["summary"],
		};
		rootChildren.push("summary-box");
		rootChildren.push(spacer(elements, "s2", 24));
	}

	// Score — secondary, compact
	if (cfg.showScore) {
		elements["score"] = {
			type: "DramaScore",
			props: {
				score: data.score,
				category,
				size: "sm",
				showLabel: false,
			},
			children: [],
		};
		elements["score-label"] = {
			type: "Text",
			props: {
				text: `${categoryLabel} • ${data.score.toFixed(1)}/10`,
				fontSize: 16,
				color: GRADIENT_TOKENS.textMuted,
			},
			children: [],
		};
		elements["score-row"] = {
			type: "Row",
			props: { alignItems: "center", gap: 16 },
			children: ["score", "score-label"],
		};
		rootChildren.push("score-row");
	}

	// Footer
	appendFooter(
		elements,
		rootChildren,
		cfg.footerText,
		GRADIENT_TOKENS.divider,
		GRADIENT_TOKENS.textMuted,
		"rgba(196, 181, 253, 0.4)",
	);

	// Root — gradient background simulated via linear-gradient string
	elements["root"] = {
		type: "Frame",
		props: {
			...dims,
			backgroundColor: GRADIENT_TOKENS.bgFrom,
			backgroundImage: `linear-gradient(135deg, ${GRADIENT_TOKENS.bgFrom} 0%, ${GRADIENT_TOKENS.bgTo} 100%)`,
			padding: 60,
			display: "flex",
			flexDirection: "column",
		},
		children: rootChildren,
	};

	return {
		version: 1,
		root: "root",
		elements,
		metadata: buildMetadata(data, cfg, "gradient-drama", "narrative-first"),
	};
}

// ============================================================================
// Shared layout helpers
// ============================================================================

/** Add a spacer element and return its key */
function spacer(
	elements: Record<string, SpecElement>,
	key: string,
	height: number,
): string {
	elements[key] = {
		type: "Spacer",
		props: { height },
		children: [],
	};
	return key;
}

/** Append footer section (divider + site + tagline) */
function appendFooter(
	elements: Record<string, SpecElement>,
	rootChildren: string[],
	footerText: string,
	dividerColor: string,
	siteColor: string,
	taglineColor: string,
): void {
	elements["footer-divider"] = {
		type: "Divider",
		props: {
			color: dividerColor,
			thickness: 2,
			marginTop: 12,
			marginBottom: 12,
		},
		children: [],
	};
	rootChildren.push("footer-divider");

	elements["footer-site"] = {
		type: "Text",
		props: { text: "dramadetector.com", fontSize: 18, color: siteColor },
		children: [],
	};
	elements["footer-tagline"] = {
		type: "Text",
		props: { text: footerText, fontSize: 16, color: taglineColor },
		children: [],
	};
	elements["footer"] = {
		type: "Row",
		props: { justifyContent: "space-between", alignItems: "center" },
		children: ["footer-site", "footer-tagline"],
	};
	rootChildren.push("footer");
}

// ============================================================================
// Router — maps artDirection → builder
// ============================================================================

/** Art direction → spec builder mapping */
export const SPEC_BUILDERS: Record<
	ArtDirection,
	(data: OGImageData, config?: SpecBuilderConfig) => ThumbnailSpecV1
> = {
	"editorial-dark": buildEditorialDarkSpec,
	"clean-analytic": buildCleanAnalyticSpec,
	"alert-high-contrast": buildAlertHighContrastSpec,
	"gradient-drama": buildGradientDramaSpec,
};

/**
 * Build a ThumbnailSpecV1 for the given art direction.
 *
 * This is the primary entry point used by the OG image handler after
 * the decision engine selects a candidate with an artDirection.
 *
 * @param artDirection - Visual treatment family
 * @param data - Drama analysis data
 * @param config - Optional overrides
 */
export function buildSpecForArtDirection(
	artDirection: ArtDirection,
	data: OGImageData,
	config?: SpecBuilderConfig,
): ThumbnailSpecV1 {
	const builder = SPEC_BUILDERS[artDirection];
	return builder(data, config);
}
